import { useProjectValue } from "components/projects/ProjectDraft";
// src/components/TranslationActionsPanel.tsx
import React, { useEffect, useState } from "react";
import axios from "../utils/axiosConfig";
import { Button } from "components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { RefreshCw, Languages as LanguagesIcon, Headphones, FileText, AlertTriangle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import { toast } from "hooks/use-toast";
import { languages } from "../data/languages";
import { Input } from "components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";

interface ITranslation {
  id: number;
  translation_progress?: number;
  speech_generation_progress?: number;
  file_id: number;
  target_language: string;
  original_language?: string;
}
interface ICost {
  translation: string;
  voiceover: string;
}
interface Props {
  translation: ITranslation;
  cost: ICost | null;
}

const TranslationActionsPanel: React.FC<Props> = ({ translation, cost }) => {
  const [actionLoading, setActionLoading] = useState(false);
  const [tags, setTags] = useState<{ id: string; name: string }[]>([]);
  const [newTagName, setNewTagName] = useProjectValue("actions.newTagName", "");
  const [selectedTagId, setSelectedTagId] = useProjectValue("actions.selectedTagId", "");

  /* ─── translation options ─── */
  const [translationLanguage, setTranslationLanguage] = useProjectValue("actions.translationLanguage", "");
  const [translationMode, setTranslationMode] = useProjectValue("actions.translationMode", "original");

  /* ─── step-by-step voiceover ─── */
  const [stepByStep, setStepByStep] = useProjectValue("actions.stepByStep", false);

  const isOriginalOnlyArtifact = translation.target_language === "original";

  const handleTranslate = async () => {
    if (!translationLanguage) return;
    setActionLoading(true);
    try {
      if (isOriginalOnlyArtifact || translationMode === "original") {
        // Original translation based on source file
        await axios.post(`/v2/file/${translation.file_id}/action`, {
          action: "translation",
          language: translationLanguage,
          tag: newTagName || selectedTagId || undefined,
        });
      } else {
        // New translation based on the current translated text
        await axios.post(`/v2/translation/${translation.id}/translate`, {
          action: "translation",
          language: translationLanguage,
          tag: newTagName || selectedTagId || undefined,
        });
      }
      toast({ title: "Success", description: "Translation started!" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to start translation",
        variant: "destructive"
      });
      console.error("Translation error:", error);
    } finally {
      setActionLoading(false);
    }
  };

  /* ─── voice‑over ─── */
  const [voices, setVoices] = useState<{ id: string; name: string }[]>([]);
  const [selectedVoice, setSelectedVoice] = useProjectValue("actions.selectedVoice", "");
  const [hideVoiceoverButton, setHideVoiceoverButton] = useState(false);
  const [voiceoverQuotaWarning, setVoiceoverQuotaWarning] = useState<string>("");

  useEffect(() => {
    axios.get<{ id: string; name: string }[]>("/v2/user/languages").then(({ data }) => {
      setVoices(data);
      if (data.length) setSelectedVoice(previous => previous || data[0].id);
    });
  }, []);
  // Refresh cost (and thus quota) when selectedVoice changes
  useEffect(() => {
    if (!translation?.file_id) return;
    axios
      .get(`/file/${translation.file_id}/cost`, {
        params: selectedVoice ? { voiceId: selectedVoice } : undefined,
      })
      .then(({ data }) => {
        if (data?.voiceover_quota && data.voiceover_quota.ok === false) {
          setVoiceoverQuotaWarning(data.voiceover_quota.message || "ElevenLabs quota exceeded.");
        } else {
          setVoiceoverQuotaWarning("");
        }
      })
      .catch(() => setVoiceoverQuotaWarning(""));
  }, [selectedVoice, translation?.file_id]);

  // Fetch available tags
  useEffect(() => {
    axios
      .get<{ id: string; name: string }[]>("/v2/user/tags")
      .then(({ data }) => {
        setTags(data);
      })
      .catch(error => {
        console.error("Error fetching tags:", error);
      });
  }, []);

  const handleVoiceover = async () => {
    if (!selectedVoice) return;
    setActionLoading(true);
    setHideVoiceoverButton(true);
    try {
      await axios.post(`/v2/generate_speech/${translation.id}`, {
        languageId: selectedVoice,
        tag: newTagName || selectedTagId || undefined,
        step_by_step: stepByStep,
      });
      toast({ title: "Success", description: "Voice‑over started!" });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="mt-6 space-y-6">
      {/* Translation */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Translation</h3>

        {isOriginalOnlyArtifact ? (
          <p className="text-sm text-muted-foreground mb-3">
            Translate the source content to a new language.
          </p>
        ) : (
          <Tabs defaultValue="original" onValueChange={setTranslationMode}>
            <TabsList className="mb-3">
              <TabsTrigger value="original">Based on original</TabsTrigger>
              <TabsTrigger value="translated">Based on translated text</TabsTrigger>
            </TabsList>

            <TabsContent value="original" className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Translate the original source content to a new language.
              </p>
            </TabsContent>

            <TabsContent value="translated" className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Create a new translation using the current translated text as source.
              </p>
            </TabsContent>
          </Tabs>
        )}

        <div className="flex flex-wrap gap-3 items-center">
          <Button
            onClick={handleTranslate}
            disabled={actionLoading}
            className="bg-green-600 hover:bg-green-700"
          >
            {actionLoading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Translating…
              </>
            ) : (
              <>
                {isOriginalOnlyArtifact || translationMode === "original" ? (
                  <LanguagesIcon className="h-4 w-4 mr-2" />
                ) : (
                  <FileText className="h-4 w-4 mr-2" />
                )}
                Translate
              </>
            )}
          </Button>

          <Select
            value={translationLanguage}
            onValueChange={setTranslationLanguage}
            disabled={actionLoading}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Target language" />
            </SelectTrigger>
            <SelectContent>
              {languages
                .filter(
                  (l) =>
                    l.code !== translation.target_language &&
                    (!translation.original_language ||
                      l.code !== translation.original_language)
                )
                .map((l) => (
                  <SelectItem key={l.code} value={l.code}>
                    {l.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          {cost && (
            <span className="text-sm bg-green-100 text-green-800 py-1 px-2 rounded-md">
              Cost: {cost.translation}
            </span>
          )}
          <p className="w-full text-xs text-muted-foreground">Most translations finish in 1–5 minutes. Your points cost is shown before you start.</p>
        </div>
      </div>

      {/* Voice‑over */}
      {translation.translation_progress === 100 &&
        !hideVoiceoverButton &&
        !translation.speech_generation_progress && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Voice-over</h3>
            <div className="flex flex-wrap gap-3 items-center">
              <Button
                onClick={handleVoiceover}
                disabled={actionLoading || !selectedVoice || !!voiceoverQuotaWarning}
              >
                {actionLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <Headphones className="h-4 w-4 mr-2" />
                    Voice-over
                  </>
                )}
              </Button>

              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={stepByStep}
                  onChange={(e) => setStepByStep(e.target.checked)}
                  disabled={actionLoading}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span>Step-by-step</span>
              </label>

              {voiceoverQuotaWarning && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <span className="text-sm">{voiceoverQuotaWarning}</span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              <Select
                value={selectedVoice}
                onValueChange={setSelectedVoice}
                disabled={actionLoading}
              >
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Select voice" />
                </SelectTrigger>
                <SelectContent>
                  {voices.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {cost && (
                <span className="text-sm bg-blue-100 text-blue-800 py-1 px-2 rounded-md">
                  Cost: {cost.voiceover}
                </span>
              )}
              <p className="w-full text-xs text-muted-foreground">Most recordings finish in 2–10 minutes, depending on length. Points are charged only when processing starts.</p>
            </div>
          </div>
        )}

      {/* Tag selector */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Tag</h3>
        <div className="flex items-center space-x-2">
          {/* Input for new tag name */}
          <Input
            placeholder="new tag"
            value={newTagName}
            maxLength={30}
            onChange={e => {
              const filtered = e.target.value.replace(/[^\w\s-]/g, '');
              setNewTagName(filtered);
              setSelectedTagId("");          // clear any selected existing tag
            }}
            className="w-44"
            disabled={actionLoading}
          />

          {tags.length > 0 && (
            <>
              <span className="text-sm text-muted-foreground">- or -</span>

              {/* Select for existing tags */}
              <Select
                value={selectedTagId}
                onValueChange={value => {
                  setSelectedTagId(value);
                  setNewTagName("");          // clear any typed new tag
                }}
                disabled={actionLoading}
              >
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Choose tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem key="-no tag-" value=" ">
                    - no tag -
                  </SelectItem>
                  {tags.map(tag => (
                    <SelectItem key={tag.id} value={tag.id}>
                      {tag.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranslationActionsPanel;
